import * as React from 'react';
import Box from '@mui/material/Box';
import { DataGrid } from '@mui/x-data-grid';

const ACCENT_BLUE = '#22638f';
const TEXT_MUTED = '#93a0b5';
const TEXT_BODY = '#3b4a63';
const BORDER = '#eef2f8';

function Table({
  rows = [],
  columns = [],
  getRowId,
  loading,
  rowCount,
  page = 0,
  pageSize = 10,
  pageSizeOptions = [10, 25, 50, 100],
  onPageChange,
  searchQuery = '',
}) {
  const customLocaleText = {
    noRowsLabel: 'There are no records to display',
  };

  // filter client-side against currently loaded rows (same as original GridTable)
  const filteredRows = React.useMemo(() => {
    if (!searchQuery) return rows;
    const q = searchQuery.toLowerCase();
    return rows.filter((row) =>
      columns.some((column) => {
        const value = row[column.field];
        return value != null && value.toString().toLowerCase().includes(q);
      })
    );
  }, [rows, columns, searchQuery]);

  const handlePaginationModelChange = (model) => {
    onPageChange?.(model.page, model.pageSize);
  };

  return (
    <Box>
      <DataGrid
        getRowId={getRowId}
        rows={filteredRows}
        columns={columns}
        autoHeight
        paginationMode={onPageChange ? 'server' : 'client'}
        rowCount={onPageChange ? rowCount : undefined}
        paginationModel={onPageChange ? { page, pageSize } : undefined}
        onPaginationModelChange={onPageChange ? handlePaginationModelChange : undefined}
        pageSizeOptions={pageSizeOptions}
        disableRowSelectionOnClick
        disableColumnMenu
        localeText={customLocaleText}
        loading={loading}
        columnHeaderHeight={40}
        rowHeight={40}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'row-even' : 'row-odd'
        }
        sx={{
          border: 'none',
          fontSize: '13.5px',
          color: TEXT_BODY,
          '& .MuiDataGrid-main': { marginTop: 0 },
          '& .MuiDataGrid-virtualScroller': { marginTop: '0 !important' },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#fafcff',
            borderBottom: `1px solid ${BORDER}`,
          },
          '& .MuiDataGrid-columnHeaderTitleContainer': { padding: 0 },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontSize: '11.5px',
            fontWeight: 700,
            color: TEXT_MUTED,
            letterSpacing: '0.05em',
            whiteSpace: 'normal',
            lineHeight: 1.2,
            overflow: 'visible',
          },
          '& .MuiDataGrid-cell': {
            borderBottom: 'none',
            padding: '0 24px',
            display: 'flex',
            alignItems: 'center',
            minHeight: '40px !important',
          },
          '& .MuiDataGrid-row': {
            minHeight: '40px !important',
            maxHeight: '40px !important',
          },
          '& .MuiDataGrid-columnHeader': { padding: '0 24px' },
          '& .MuiDataGrid-columnSeparator': { display: 'none' },
          '& .row-even': { backgroundColor: '#f8fbfe' },
          '& .row-odd': { backgroundColor: '#ffffff' },
          '& .MuiDataGrid-row:hover': { backgroundColor: '#eef4fb !important' },
          '& .MuiDataGrid-footerContainer': { borderTop: `1px solid ${BORDER}` },
          '& .MuiTablePagination-root': { fontSize: '12.5px', color: TEXT_MUTED },
          '& .MuiTablePagination-select, & .MuiTablePagination-actions button': {
            color: ACCENT_BLUE,
          },
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
          '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
            outline: 'none',
          },
        }}
      />
    </Box>
  );
}

export default Table;